import 'dayjs/locale/sv';
import 'dayjs/locale/nb';

import type { RemovableRef} from '@vueuse/core';
import { StorageSerializers } from '@vueuse/core';
import { defineStore } from 'pinia';
import type { Notification, NotificationClickEvent } from '@/models/common/Notification';
import { Guid } from 'guid-typescript';
import type { NotificationStates } from '@/models/enums/NotificationStates';
import { useDomainStorage } from '@/composables/useDomainStorage';
import { useLoginStore } from '@/login/stores/useLoginStore';

type NotificationStoreState = {
    _notifications: RemovableRef<Notification[]>
};

export const useNotificationStore = defineStore('notification', {
    state: (): NotificationStoreState => ({
        _notifications: useDomainStorage<Notification[]>('notifications', [], localStorage, { serializer: StorageSerializers.object })
    }),
    getters: {
        notifications: state => state._notifications.filter(x => x.autoremove !== true),
        previewNotifications: state => state._notifications.filter(x => !x.isPreviewHidden)
    },
    actions: {
        add(notification: Notification): void {
            if (useLoginStore().isLoggedIn === false) return;
            this.$state._notifications.unshift(notification);
        },
        showQuickMessage(message: string, state: NotificationStates): void {
            this.addByParams(Guid.create(), message, state, false, undefined, undefined, false, true);
        },
        addByParams(id: Guid | string, message: string, state: NotificationStates, isPreviewHidden: boolean = false, additionalMessage?: string, additionalOnClick?: NotificationClickEvent, sticky?: boolean, autoremove?: boolean) {
            if (useLoginStore().isLoggedIn === false) return;
            const notification: Notification = {
                id,
                message,
                state,
                isPreviewHidden,
                created: new Date(),
                additionalMessage,
                additionalOnClick,
                sticky,
                autoremove
            };
            this.$state._notifications.unshift(notification);
        },
        remove(id: Guid | string) {
            const current = this.$state._notifications.findIndex(c => c.id === id);

            if (current >= 0) {
                this.$state._notifications.splice(current, 1);
            }
        },
        clear() {
            this.$state._notifications = [];
        },
        hide(id: Guid | string) {
            const current = this.$state._notifications.find(c => c.id === id);

            if (current != null) {
                current.isPreviewHidden = true;

                if (current.autoremove === true) {
                    this.remove(id);
                }
            }
        }
    }
});
